
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
  Checkbox,
  SelectChangeEvent,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { useSettingsContext } from "../SettingsProvider";
import { SettingsPageHeader, FloatingSaveButton } from "../components";
import { v4 as uuid } from "uuid";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AddIcon } from "@/assets/icons";
import { Icon } from "@/components/Icon";
import { COLOUR_PALETTES, getTriageLevelColours, type ColourPaletteName } from "@/util/triage";

type TriageLevel = {
  id: string;
  label: string;
  value: number;
  colourPalette: ColourPaletteName;
};

type ObservationLevel = {
  id: string;
  label: string;
  triageLevel: number[];
  firstInterval: number;
  subsequentInterval: number;
};

type TriageReason = {
  id: string;
  code: string;
  description: string;
};

type EncounterSettings = {
  triageLevels: TriageLevel[];
  observations: {
    preTriageDefault?: string;
    updateWindow?: number;
    levels: ObservationLevel[];
  };
};

const TriageLevelRow = ({
  level,
  triageLevels,
  onUpdate,
  onDelete
}: {
  level: TriageLevel;
  triageLevels: TriageLevel[];
  onUpdate: (level: TriageLevel) => void;
  onDelete: (id: string) => void;
}) => {
  const [label, setLabel] = useState(level.label);
  const [value, setValue] = useState(level.value);
  const [colourPalette, setColourPalette] = useState<ColourPaletteName>(level.colourPalette);

  const handleLabelChange = (newLabel: string) => {
    setLabel(newLabel);
    onUpdate({ ...level, label: newLabel });
  };

  const handleValueChange = (newValue: number) => {
    setValue(newValue);
    onUpdate({ ...level, value: newValue });
  };

  const handleColourChange = (newColourPalette: ColourPaletteName) => {
    setColourPalette(newColourPalette);
    onUpdate({ ...level, colourPalette: newColourPalette });
  };

  const handleDelete = () => {
    onDelete(level.id);
  };

  const colours = getTriageLevelColours(colourPalette);

  return (
    <Card variant="outlined" sx={{ border: `2px solid ${colours?.border}` }}>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                label="Label"
                value={label}
                onChange={(e) => handleLabelChange(e.target.value)}
                variant="outlined"
              />
            </Box>
            <Box sx={{ width: 150 }}>
              <TextField
                fullWidth
                label="Value"
                type="number"
                value={value}
                onChange={(e) => handleValueChange(Number(e.target.value))}
                variant="outlined"
              />
            </Box>
            <Box sx={{ width: 200 }}>
              <FormControl fullWidth>
                <Select
                  value={colourPalette}
                  onChange={(e) => handleColourChange(e.target.value as ColourPaletteName)}
                  input={<OutlinedInput />}
                >
                  {Object.keys(COLOUR_PALETTES).map((paletteName) => (
                    <MenuItem key={paletteName} value={paletteName}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            backgroundColor: COLOUR_PALETTES[paletteName as ColourPaletteName].border,
                            border: `2px solid ${COLOUR_PALETTES[paletteName as ColourPaletteName].text}`,
                          }}
                        />
                        {paletteName.charAt(0).toUpperCase() + paletteName.slice(1)}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box>
              <Button variant="outlined" onClick={handleDelete}>
                Delete
              </Button>
            </Box>
          </Stack>
          {/* Color Preview */}
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              backgroundColor: colours?.background,
              border: `1px solid ${colours?.border}`,
              color: colours?.text,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              Preview: {label || "Triage Level"} (Value: {value})
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const TriageReasonRow = ({
  reason,
  onUpdate,
  onDelete
}: {
  reason: TriageReason;
  onUpdate: (reason: TriageReason) => void;
  onDelete: (id: string) => void;
}) => {
  const [code, setCode] = useState(reason.code);
  const [description, setDescription] = useState(reason.description);

  const handleCodeChange = (newCode: string) => {
    setCode(newCode);
    onUpdate({ ...reason, code: newCode });
  };

  const handleDescriptionChange = (newDescription: string) => {
    setDescription(newDescription);
    onUpdate({ ...reason, description: newDescription });
  };

  const handleDelete = () => {
    onDelete(reason.id);
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
            <Box sx={{ width: 150 }}>
              <TextField
                fullWidth
                label="Code"
                value={code}
                onChange={(e) => handleCodeChange(e.target.value)}
                variant="outlined"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                label="Description"
                value={description}
                onChange={(e) => handleDescriptionChange(e.target.value)}
                variant="outlined"
              />
            </Box>
            <Box>
              <Button variant="outlined" onClick={handleDelete}>
                Delete
              </Button>
            </Box>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

const ObservationLevelRow = ({
  level,
  triageLevels,
  onUpdate,
  onDelete
}: {
  level: ObservationLevel;
  triageLevels: TriageLevel[];
  onUpdate: (level: ObservationLevel) => void;
  onDelete: (id: string) => void;
}) => {
  const [label, setLabel] = useState(level.label);
  const [selectedTriageLevels, setSelectedTriageLevels] = useState<number[]>(level.triageLevel);
  const [firstInterval, setFirstInterval] = useState(level.firstInterval);
  const [subsequentInterval, setSubsequentInterval] = useState(level.subsequentInterval);

  const handleTriageLevelChange = (event: SelectChangeEvent<number[]>) => {
    const newTriageLevels = event.target.value as number[];
    setSelectedTriageLevels(newTriageLevels);
    onUpdate({
      ...level,
      triageLevel: newTriageLevels
    });
  };

  const handleLabelChange = (newLabel: string) => {
    setLabel(newLabel);
    onUpdate({ ...level, label: newLabel });
  };

  const handleFirstIntervalChange = (newInterval: number) => {
    setFirstInterval(newInterval);
    onUpdate({
      ...level,
      firstInterval: newInterval
    });
  };

  const handleSubsequentIntervalChange = (newInterval: number) => {
    setSubsequentInterval(newInterval);
    onUpdate({
      ...level,
      subsequentInterval: newInterval
    });
  };

  const handleDelete = () => {
    onDelete(level.id);
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                label="Label"
                value={label}
                onChange={(e) => handleLabelChange(e.target.value)}
                variant="outlined"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FormControl fullWidth>
                <Select
                  multiple
                  value={selectedTriageLevels}
                  onChange={handleTriageLevelChange}
                  input={<OutlinedInput />}
                  renderValue={(selected) => (
                    <Box>
                      {selected.map((value) => {
                        const triageLevel = triageLevels.find(tl => tl.value === value);
                        return triageLevel?.label || `Value ${value}`;
                      }).join(", ")}
                    </Box>
                  )}
                >
                  {triageLevels.map((triageLevel) => (
                    <MenuItem key={triageLevel.id} value={triageLevel.value}>
                      <Checkbox checked={selectedTriageLevels.indexOf(triageLevel.value) > -1} />
                      {triageLevel.label} (Value: {triageLevel.value})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Stack>
          <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                label="First Interval (min)"
                type="number"
                value={firstInterval}
                onChange={(e) => handleFirstIntervalChange(Number(e.target.value))}
                variant="outlined"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                label="Subsequent Interval (min)"
                type="number"
                value={subsequentInterval}
                onChange={(e) => handleSubsequentIntervalChange(Number(e.target.value))}
                variant="outlined"
              />
            </Box>
            <Box>
              <Button variant="outlined" onClick={handleDelete}>
                Delete
              </Button>
            </Box>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export const EncountersSettings = () => {
  const { settings, setSettings } = useSettingsContext();

  // Memoize original values to prevent infinite loops
  const originalEncounterSettings = useMemo(() => {
    const encounterSettings = (settings as any)?.encounter;
    const triageSettings = (settings as any)?.triage;
    return {
      triageLevels: encounterSettings?.triageLevels ?? [],
      observations: {
        preTriageDefault: encounterSettings?.observations?.preTriageDefault,
        updateWindow: encounterSettings?.observations?.updateWindow,
        levels: encounterSettings?.observations?.levels ?? []
      },
      triageUpdateWindow: triageSettings?.updateWindow,
      triageReasons: triageSettings?.reasons ?? []
    } as EncounterSettings & { triageUpdateWindow?: number; triageReasons: TriageReason[] };
  }, [settings]);

  // Local state for form data
  const [triageLevels, setTriageLevels] = useState<TriageLevel[]>([]);
  const [observationLevels, setObservationLevels] = useState<ObservationLevel[]>([]);
  const [preTriageDefault, setPreTriageDefault] = useState<string>("");
  const [updateWindow, setUpdateWindow] = useState<number>(0);
  const [updateWindowInput, setUpdateWindowInput] = useState<string>("");
  const [triageUpdateWindow, setTriageUpdateWindow] = useState<number>(3);
  const [triageUpdateWindowInput, setTriageUpdateWindowInput] = useState<string>("");
  const [triageReasons, setTriageReasons] = useState<TriageReason[]>([]);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Generic helpers to set/clear validation errors for any field
  const setFieldError = (field: string, message?: string) => {
    setValidationErrors(prev => {
      const next = { ...prev } as Record<string, string>;
      if (!message) delete next[field]; else next[field] = message;
      return next;
    });
  };

  // Initialize local state when settings change
  useEffect(() => {
    setTriageLevels(originalEncounterSettings.triageLevels);
    setObservationLevels(originalEncounterSettings.observations.levels);
    setPreTriageDefault(originalEncounterSettings.observations.preTriageDefault || "");
    const initialUpdateWindow = originalEncounterSettings.observations.updateWindow;
    setUpdateWindow(initialUpdateWindow || 0);
    setUpdateWindowInput(
      typeof initialUpdateWindow === "number" && !isNaN(initialUpdateWindow)
        ? String(initialUpdateWindow)
        : ""
    );
    const initialTriageUpdateWindow = originalEncounterSettings.triageUpdateWindow ?? 3;
    setTriageUpdateWindow(initialTriageUpdateWindow);
    setTriageUpdateWindowInput(
      typeof initialTriageUpdateWindow === "number" && !isNaN(initialTriageUpdateWindow)
        ? String(initialTriageUpdateWindow)
        : "3"
    );
    // Load triage reasons, converting from settings format to component format
    const reasonsFromSettings = originalEncounterSettings.triageReasons || [];
    const reasonsWithIds = reasonsFromSettings.map((r: any) => ({
      id: r.id || uuid(),
      code: r.code || "",
      description: r.description || ""
    }));
    setTriageReasons(reasonsWithIds);
    setIsInitialized(true);
  }, [originalEncounterSettings]);

  // Calculate if data is dirty using useMemo to prevent infinite loops
  const isDirty = useMemo(() => {
    // Only check for changes if we have initialized the local state
    if (!isInitialized) {
      return false;
    }

    const parsedUpdateWindow = updateWindowInput.trim() === "" ? undefined : Number(updateWindowInput);
    const parsedTriageUpdateWindow = triageUpdateWindowInput.trim() === "" ? undefined : Number(triageUpdateWindowInput);

    const currentSettings = {
      triageLevels,
      observations: {
        preTriageDefault: preTriageDefault || undefined,
        updateWindow: parsedUpdateWindow,
        levels: observationLevels
      },
      triageUpdateWindow: parsedTriageUpdateWindow,
      triageReasons: triageReasons.map(r => ({ code: r.code, description: r.description }))
    };

    const originalSettings = {
      triageLevels: originalEncounterSettings.triageLevels,
      observations: {
        preTriageDefault: originalEncounterSettings.observations.preTriageDefault || undefined,
        updateWindow: originalEncounterSettings.observations.updateWindow || undefined,
        levels: originalEncounterSettings.observations.levels
      },
      triageUpdateWindow: originalEncounterSettings.triageUpdateWindow ?? 3,
      triageReasons: (originalEncounterSettings.triageReasons || []).map((r: any) => ({ 
        code: r.code || "", 
        description: r.description || "" 
      }))
    };

    return JSON.stringify(currentSettings) !== JSON.stringify(originalSettings);
  }, [triageLevels, observationLevels, preTriageDefault, updateWindowInput, triageUpdateWindowInput, triageReasons, originalEncounterSettings, isInitialized]);

  const addTriageLevel = () => {
    const newLevel: TriageLevel = {
      id: uuid(),
      label: "",
      value: 1,
      colourPalette: "royalblue" // Default colour
    };
    setTriageLevels([...triageLevels, newLevel]);
  };

  const addObservationLevel = () => {
    const newId = (observationLevels.length + 1).toString();
    const newLevel: ObservationLevel = {
      id: newId,
      label: "",
      triageLevel: [],
      firstInterval: 60,
      subsequentInterval: 60
    };
    setObservationLevels([...observationLevels, newLevel]);
  };

  const updateTriageLevel = (updatedLevel: TriageLevel) => {
    setTriageLevels(levels =>
      levels.map(level => level.id === updatedLevel.id ? updatedLevel : level)
    );
  };

  const updateObservationLevel = (updatedLevel: ObservationLevel) => {
    setObservationLevels(levels =>
      levels.map(level => level.id === updatedLevel.id ? updatedLevel : level)
    );
  };

  const deleteTriageLevel = (id: string) => {
    setTriageLevels(levels => levels.filter(level => level.id !== id));
  };

  const deleteObservationLevel = (id: string) => {
    const updatedLevels = observationLevels.filter(level => level.id !== id);
    // Reassign sequential IDs
    const renumberedLevels = updatedLevels.map((level, index) => ({
      ...level,
      id: (index + 1).toString()
    }));
    setObservationLevels(renumberedLevels);

    // Clear pre-triage default if it was the deleted level
    if (preTriageDefault === id) {
      setPreTriageDefault("");
    }
  };

  const addTriageReason = () => {
    const newReason: TriageReason = {
      id: uuid(),
      code: "",
      description: ""
    };
    setTriageReasons([...triageReasons, newReason]);
  };

  const updateTriageReason = (updatedReason: TriageReason) => {
    setTriageReasons(reasons =>
      reasons.map(reason => reason.id === updatedReason.id ? updatedReason : reason)
    );
  };

  const deleteTriageReason = (id: string) => {
    setTriageReasons(reasons => reasons.filter(reason => reason.id !== id));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Validate before save
      const parsedUpdateWindow = updateWindowInput.trim() === "" ? undefined : Number(updateWindowInput);
      if (updateWindowInput.trim() !== "" && (isNaN(parsedUpdateWindow as number) || (parsedUpdateWindow as number) < 0)) {
        setFieldError("updateWindow", "Invalid value");
        return;
      }
      // clear any prior error on successful validation
      setFieldError("updateWindow");

      const parsedTriageUpdateWindow = triageUpdateWindowInput.trim() === "" ? undefined : Number(triageUpdateWindowInput);
      if (triageUpdateWindowInput.trim() !== "" && (isNaN(parsedTriageUpdateWindow as number) || (parsedTriageUpdateWindow as number) < 0)) {
        setFieldError("triageUpdateWindow", "Invalid value");
        return;
      }
      // clear any prior error on successful validation
      setFieldError("triageUpdateWindow");

      await setSettings({
        encounter: {
          triageLevels,
          observations: {
            preTriageDefault: preTriageDefault || undefined,
            updateWindow: parsedUpdateWindow,
            levels: observationLevels
          }
        },
        triage: {
          updateWindow: parsedTriageUpdateWindow ?? 3,
          reasons: triageReasons.map(r => ({
            code: r.code,
            description: r.description
          }))
        }
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ position: "relative", height: "100%", width: "100%", overflow: "hidden" }}>
      <Box sx={{ height: "100%", width: "100%", overflow: "auto", p: 3, pb: 10 }}>
        <Stack spacing={4} sx={{ width: "100%" }}>
          {/* Header with dirty indicator */}
          <SettingsPageHeader title="Encounters Settings" isDirty={isDirty} />

          {/* Triage Levels Section */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h5">Triage Levels</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addTriageLevel}
                  >
                    Add Triage Level
                  </Button>
                </Stack>

                {triageLevels.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                    No triage levels configured. Click &quot;Add Triage Level&quot; to get started.
                  </Typography>
                ) : (
                  <Stack spacing={2}>
                    {triageLevels.map((level) => (
                      <TriageLevelRow
                        key={level.id}
                        level={level}
                        triageLevels={triageLevels}
                        onUpdate={updateTriageLevel}
                        onDelete={deleteTriageLevel}
                      />
                    ))}
                  </Stack>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Timeout Settings Section */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h5">Timeout Settings</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {/* Triage Update Window Setting */}
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Triage Update Window (Minutes)
                  </Typography>
                  <TextField
                    fullWidth
                    label="Triage Update Window"
                    type="text"
                    value={triageUpdateWindowInput}
                    onChange={(e) => {
                      const raw = e.target.value;
                      // allow empty; otherwise digits only
                      if (raw === "") {
                        setTriageUpdateWindowInput("");
                        setTriageUpdateWindow(3);
                        setFieldError("triageUpdateWindow");
                        return;
                      }
                      const digitsOnly = raw.replace(/\D+/g, "");
                      setTriageUpdateWindowInput(digitsOnly);
                      if (digitsOnly === "") {
                        setTriageUpdateWindow(3);
                        setFieldError("triageUpdateWindow");
                      } else {
                        const num = Number(digitsOnly);
                        setTriageUpdateWindow(num);
                        setFieldError("triageUpdateWindow", num < 0 ? "Invalid value" : undefined);
                      }
                    }}
                    onBlur={() => {
                      const trimmed = triageUpdateWindowInput.trim();
                      if (trimmed === "") {
                        setFieldError("triageUpdateWindow");
                        return;
                      }
                      // normalize leading zeros
                      const normalized = String(Number(trimmed));
                      setTriageUpdateWindowInput(normalized);
                      const num = Number(normalized);
                      setTriageUpdateWindow(num);
                      setFieldError("triageUpdateWindow", num < 0 ? "Invalid value" : undefined);
                    }}
                    variant="outlined"
                    error={Boolean(validationErrors.triageUpdateWindow)}
                    helperText={validationErrors.triageUpdateWindow || "Time window for triage updates"}
                    sx={{ maxWidth: 300 }}
                    inputMode="numeric"
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*", min: 0, step: 1 }}
                  />
                </Box>
                {/* Update Window Setting */}
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Observation Update Window (minutes)
                  </Typography>
                  <TextField
                    fullWidth
                    label="Update Window"
                    type="text"
                    value={updateWindowInput}
                    onChange={(e) => {
                      const raw = e.target.value;
                      // allow empty; otherwise digits only
                      if (raw === "") {
                        setUpdateWindowInput("");
                        setUpdateWindow(0);
                        setFieldError("updateWindow");
                        return;
                      }
                      const digitsOnly = raw.replace(/\D+/g, "");
                      setUpdateWindowInput(digitsOnly);
                      if (digitsOnly === "") {
                        setUpdateWindow(0);
                        setFieldError("updateWindow");
                      } else {
                        const num = Number(digitsOnly);
                        setUpdateWindow(num);
                        setFieldError("updateWindow", num < 0 ? "Invalid value" : undefined);
                      }
                    }}
                    onBlur={() => {
                      const trimmed = updateWindowInput.trim();
                      if (trimmed === "") {
                        setFieldError("updateWindow");
                        return;
                      }
                      // normalize leading zeros
                      const normalized = String(Number(trimmed));
                      setUpdateWindowInput(normalized);
                      const num = Number(normalized);
                      setUpdateWindow(num);
                      setFieldError("updateWindow", num < 0 ? "Invalid value" : undefined);
                    }}
                    variant="outlined"
                    error={Boolean(validationErrors.updateWindow)}
                    helperText={validationErrors.updateWindow || "Time window for observation updates"}
                    sx={{ maxWidth: 300 }}
                    inputMode="numeric"
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*", min: 0, step: 1 }}
                  />
                </Box>
                {/* Triage Reasons Section */}
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Triage Reasons
                  </Typography>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={addTriageReason}
                      >
                        Add Triage Reason
                      </Button>
                    </Stack>

                    {triageReasons.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                        No triage reasons configured. Click &quot;Add Triage Reason&quot; to get started.
                      </Typography>
                    ) : (
                      <Stack spacing={2}>
                        {triageReasons.map((reason) => (
                          <TriageReasonRow
                            key={reason.id}
                            reason={reason}
                            onUpdate={updateTriageReason}
                            onDelete={deleteTriageReason}
                          />
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Observation Levels Section */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h5">Observation Levels</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addObservationLevel}
                    disabled={triageLevels.length === 0}
                  >
                    Add Observation Level
                  </Button>
                </Stack>

                {triageLevels.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    Please add triage levels first before configuring observation levels.
                  </Typography>
                )}

                {/* Default for Pre-Triage */}
                {observationLevels.length > 0 && (
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Default for Pre-Triage
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        value={preTriageDefault}
                        onChange={(e) => setPreTriageDefault(e.target.value)}
                        displayEmpty
                        input={<OutlinedInput />}
                      >
                        <MenuItem value="">
                          <em>Select default observation level for pre-triage</em>
                        </MenuItem>
                        {observationLevels.map((level) => (
                          <MenuItem key={level.id} value={level.id}>
                            {level.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}

                {observationLevels.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                    No observation levels configured. Click &quot;Add Observation Level&quot; to get started.
                  </Typography>
                ) : (
                  <Stack spacing={2}>
                    {observationLevels.map((level) => (
                      <ObservationLevelRow
                        key={level.id}
                        level={level}
                        triageLevels={triageLevels}
                        onUpdate={updateObservationLevel}
                        onDelete={deleteObservationLevel}
                      />
                    ))}
                  </Stack>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>

        {/* Floating Save Button */}
        <FloatingSaveButton show={isDirty} onSave={handleSave} isSaving={isSaving} />
      </Box>
    </Box>
  );
};